import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Tutorial steps configuration
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to GuapGoals! 🎉',
    content: "Let's take a quick tour to help you get started with managing your finances. This will only take a minute!",
    icon: '👋',
    tip: null,
    target: null, // center modal
    route: '/app/dashboard',
  },
  {
    id: 'streak',
    title: '🔥 Street Cred System',
    content: 'Build your streak by logging your finances daily! Watch your status grow from Paper Chaser to Mogul Status.',
    icon: '🔥',
    tip: 'At 14 days, you earn a "Bounce Back" - a streak freeze for when life happens!',
    target: '[data-testid="streak-indicator"]',
    route: '/app/dashboard',
  },
  {
    id: 'income',
    title: 'Step 1: Add Your Income',
    content: 'Start by adding your income sources. This tells GuapGoals how much money you have coming in each pay period.',
    icon: '💵',
    tip: 'Add all your income sources: salary, side gigs, freelance work, etc.',
    target: '[data-tutorial="income-nav"]',
    route: '/app/income',
  },
  {
    id: 'bills',
    title: 'Step 2: Add Your Bills',
    content: 'Add your recurring bills like rent, utilities, and subscriptions. Mark bills as PAID with a green checkmark!',
    icon: '📋',
    tip: "Pro tip: Don't add loans here - add them in Debt Snowball instead!",
    target: '[data-tutorial="bills-nav"]',
    route: '/app/bills',
  },
  {
    id: 'goals',
    title: 'Step 3: Set Your Goals',
    content: "What are you saving for? Set goals for things like a vacation or new car.",
    icon: '🎯',
    tip: "Start building your Savings Stash!",
    target: '[data-tutorial="goals-nav"]',
    route: '/app/goals',
  },
  {
    id: 'debt',
    title: 'Step 4: Track Your Debt',
    content: 'Add your loans and credit cards here. GuapGoals will create a payoff plan.',
    icon: '💳',
    tip: 'Mark debts as paid to track progress!',
    target: '[data-tutorial="debt-nav"]',
    route: '/app/debt-snowball',
  },
  {
    id: 'dashboard',
    title: 'Step 5: Your Dashboard',
    content: 'This is your command center! Mark bills as paid and track your streak.',
    icon: '📊',
    tip: 'Hit "No Spend Today" to keep your streak going!',
    target: '[data-tutorial="dashboard-nav"]',
    route: '/app/dashboard',
  },
  {
    id: 'security',
    title: '🔒 Security First',
    content: 'Your data is secured and encrypted. We never connect to your bank.',
    icon: '🔐',
    tip: 'Visit Security page for more details.',
    target: '[data-testid="security-badge"]',
    route: '/app/security',
  },
  {
    id: 'complete',
    title: "You're All Set! 🚀",
    content: 'Great job! Build your streak, mark payments as done, and get your money right!',
    icon: '✨',
    tip: 'Click the chat button to talk to Guapo anytime!',
    target: null, // center modal
    route: '/app/dashboard',
  },
];

// Main Tutorial Component - Simplified for mobile
export function Tutorial({ onComplete }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = TUTORIAL_STEPS[currentStep];
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  // Navigate to correct route
  useEffect(() => {
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [currentStep, step.route, location.pathname, navigate]);

  // Scroll to target element
  useEffect(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }, [currentStep, step.target]);

  const handleNext = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      const nextStep = TUTORIAL_STEPS[currentStep + 1];
      // Navigate to the next step's route before updating state
      if (nextStep.route && location.pathname !== nextStep.route) {
        navigate(nextStep.route);
      }
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, location.pathname, navigate]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = TUTORIAL_STEPS[currentStep - 1];
      // Navigate to the previous step's route
      if (prevStep.route && location.pathname !== prevStep.route) {
        navigate(prevStep.route);
      }
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, location.pathname, navigate]);

  const handleComplete = useCallback(() => {
    localStorage.setItem('guapgoals_tutorial_complete', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('guapgoals_tutorial_complete', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <AnimatePresence mode="wait">
      {/* Dark overlay */}
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-[9998]"
        onClick={handleSkip}
      />
      
      {/* Tutorial Card - Fixed at bottom on mobile, centered on desktop */}
      <motion.div
        key={`step-${currentStep}`}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed z-[9999] left-0 right-0 bottom-0 mx-auto p-4 sm:p-0 sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-auto"
      >
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:w-[380px] max-h-[80vh] overflow-y-auto">
          {/* Progress Bar */}
          <div className="p-4 pb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{currentStep + 1} of {TUTORIAL_STEPS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pb-2">
            {/* Icon & Title */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">{step.icon}</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">{step.title}</h3>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-3">{step.content}</p>

            {/* Tip Box */}
            {step.tip && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 flex-shrink-0">💡</span>
                  <p className="text-xs text-amber-800">{step.tip}</p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons - Always visible */}
          <div className="p-4 pt-2 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={handleSkip}
                className="text-gray-500 hover:text-gray-700 text-sm py-2"
                data-testid="tutorial-skip-btn"
              >
                Skip tour
              </button>
              
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrev}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-all text-sm font-medium"
                    data-testid="tutorial-prev-btn"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all text-sm"
                  data-testid="tutorial-next-btn"
                >
                  {currentStep === TUTORIAL_STEPS.length - 1 ? 'Done! 🚀' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Feature Tips - Keep for contextual help
const FEATURE_TIPS = {
  dashboard: {
    actionEngine: {
      title: 'Action Engine',
      description: 'Got leftover money? The Action Engine helps you decide: pay down debt, build savings, invest, or enjoy it!',
      icon: '💰',
    },
    quickExpenses: {
      title: 'Quick Expenses',
      description: 'Track daily spending like groceries, gas, and dining. These get subtracted from your leftover automatically.',
      icon: '🧾',
    },
    trueLeftover: {
      title: 'True Leftover',
      description: 'This is what you actually have left after all bills, expenses, and allocations. The real number!',
      icon: '💵',
    },
    debtSection: {
      title: 'Debt on Dashboard',
      description: 'Loans and credit cards from Debt Snowball automatically appear here. No need to add them twice!',
      icon: '💳',
    },
  },
  debtSnowball: {
    avalanche: {
      title: 'Avalanche Method',
      description: 'Pay off highest interest debt first. Saves the most money in interest charges.',
      icon: '🏔️',
    },
    snowball: {
      title: 'Snowball Method',
      description: 'Pay off smallest balance first. Quick wins keep you motivated!',
      icon: '⛄',
    },
    extraPayment: {
      title: 'Extra Payments',
      description: 'Adding even $50 extra per month can shave years off your debt!',
      icon: '⚡',
    },
    autoSync: {
      title: 'Auto-Sync to Bills',
      description: 'Debts you add here automatically show up in Bills and on your Dashboard. One entry does it all!',
      icon: '🔄',
    },
  },
  bills: {
    debtSync: {
      title: 'Synced from Debt Snowball',
      description: 'Loans and credit cards appear here automatically when added to Debt Snowball. Look for the 💳 icon!',
      icon: '🔗',
    },
  },
  envelope: {
    howItWorks: {
      title: 'How It Works',
      description: 'Each envelope has a random amount. Open one per day and save that amount. Gamified savings!',
      icon: '📨',
    },
    modes: {
      title: 'Game Modes',
      description: 'Solo: Just you. Co-op: Team up with family. Competitive: Race to finish first!',
      icon: '🎮',
    },
  },
};

// Feature Tooltip Component
export function FeatureTip({ feature, section, children, position = 'top' }) {
  const [showTip, setShowTip] = useState(false);
  const tip = FEATURE_TIPS[section]?.[feature];

  if (!tip) return children;

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
      >
        {children}
      </div>
      
      {showTip && (
        <div
          className={`
            absolute z-50 w-64 bg-gray-900 text-white rounded-lg p-3 shadow-xl
            ${positionClasses[position]}
            animate-in fade-in zoom-in-95 duration-200
          `}
        >
          <div className="flex items-center gap-2 mb-1">
            <span>{tip.icon}</span>
            <span className="font-semibold text-sm">{tip.title}</span>
          </div>
          <p className="text-xs text-gray-300">{tip.description}</p>
          {/* Arrow */}
          <div
            className={`
              absolute w-2 h-2 bg-gray-900 transform rotate-45
              ${position === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2' : ''}
              ${position === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2' : ''}
              ${position === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2' : ''}
              ${position === 'right' ? 'left-[-4px] top-1/2 -translate-y-1/2' : ''}
            `}
          />
        </div>
      )}
    </div>
  );
}

// Help Button Component
export function HelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full flex items-center justify-center transition-all"
      title="Need help?"
    >
      <span className="text-sm font-bold">?</span>
    </button>
  );
}

// Context Help Panel
export function ContextHelp({ section, isOpen, onClose }) {
  const tips = FEATURE_TIPS[section];

  if (!isOpen || !tips) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">📚 Help Guide</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center"
          >
            ✕
          </button>
        </div>
        <div className="p-4 space-y-4">
          {Object.entries(tips).map(([key, tip]) => (
            <div key={key} className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{tip.icon}</span>
                <h3 className="font-semibold text-gray-900">{tip.title}</h3>
              </div>
              <p className="text-gray-600 text-sm">{tip.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Hook to check if tutorial should show
export function useTutorial() {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('guapgoals_tutorial_complete');
    if (!completed) {
      // Small delay to let the page load first
      setTimeout(() => setShowTutorial(true), 500);
    }
  }, []);

  const resetTutorial = () => {
    localStorage.removeItem('guapgoals_tutorial_complete');
    setShowTutorial(true);
  };

  const completeTutorial = () => {
    localStorage.setItem('guapgoals_tutorial_complete', 'true');
    setShowTutorial(false);
  };

  return { showTutorial, setShowTutorial, resetTutorial, completeTutorial };
}

export default Tutorial;
